import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useBusiness } from "@/context/BusinessContext";
import { Newspaper, ArrowRight } from "lucide-react";

export default function BlogIndexPage() {
  const { business } = useBusiness();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/blog");
        // /api/blog returns a raw list (post-standardisation)
        setPosts(Array.isArray(data) ? data : (data.posts || []));
      } catch (e) {
        setError(formatApiError(e.response?.data?.detail) || "Failed to load posts");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div data-testid="blog-index-page" className="bg-[#FDFBF7]">
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 md:pt-20 pb-10">
        <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#5A7247] inline-flex items-center gap-1.5">
          <Newspaper className="h-3.5 w-3.5" strokeWidth={1.5} />
          Journal
        </p>
        <h1
          data-testid="blog-heading"
          className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-3 text-[#121614]"
        >
          From the kitchen at{" "}
          <span className="italic text-[#C05746]">
            {business?.business_name || "us"}
          </span>
        </h1>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        {loading && <p data-testid="blog-loading" className="text-[#5C625E]">Loading…</p>}
        {error && <p data-testid="blog-error" className="text-red-600">{error}</p>}
        {!loading && !error && posts.length === 0 && (
          <div
            data-testid="blog-empty"
            className="bg-white border border-[#E8E5DF] rounded-2xl p-8 text-center"
          >
            <p className="font-serif-display text-2xl text-[#121614]">
              No posts yet
            </p>
            <p className="mt-2 text-sm text-[#5C625E]">
              When the team shares stories from the pass, they&apos;ll appear here.
            </p>
          </div>
        )}
        {posts.length > 0 && (
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {posts.map((p) => (
              <li
                key={p.id}
                data-testid={`blog-post-card-${p.slug}`}
                className="group bg-white border border-[#E8E5DF] rounded-2xl overflow-hidden hover:shadow-lg hover:-translate-y-0.5 transition-all"
              >
                {p.og_image && (
                  <div className="aspect-[16/9] bg-[#F3F0EA] overflow-hidden">
                    <img
                      src={p.og_image}
                      alt={p.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                )}
                <div className="p-5">
                  <p className="text-[10px] uppercase tracking-wider font-bold text-[#5A7247]">
                    {(p.updated_at || "").slice(0, 10)}
                  </p>
                  <h2 className="mt-2 font-serif-display text-2xl text-[#121614] group-hover:text-[#C05746] transition-colors">
                    {p.title}
                  </h2>
                  {p.seo_description && (
                    <p className="mt-2 text-sm text-[#5C625E] line-clamp-3">
                      {p.seo_description}
                    </p>
                  )}
                  <Link
                    to={`/p/${p.slug}`}
                    data-testid={`blog-post-link-${p.slug}`}
                    className="mt-4 inline-flex items-center gap-1.5 text-sm text-[#C05746] hover:text-[#A64A3C] font-medium"
                  >
                    Read more
                    <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.5} />
                  </Link>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
